import Model, {BudgetType, ModelSetting} from "../../../../model/Model";
import {IBudgetCalc} from "../../../../components/calculator/Calculator";
import React, {useEffect, useState} from "react";
import Income from "../../../../model/Income";
import Deduction from "../../../../model/Deduction";
import Tax from "../../../../model/Tax";
import Expense, {ExpenseCategory} from "../../../../model/Expense";
import PrintReport from "../../../../components/page/PrintReport";
import {dateToLocalFormat} from "../../../../stores/StoreUtilities";
import {Box, FormLabel, Typography, useTheme} from "@mui/material";
import {useResources} from "../../../../stores/ResourceProvider";
import BudgetReport from "./BudgetReport";


const BudgetPrintReport = ({
  model,
  budgetCalc
} : {
  model: Model
  budgetCalc?: IBudgetCalc
}) => {
  const [incomes, setIncomes] = useState<Income[]>([])
  const [deductions, setDeductions] = useState<Deduction[]>([])
  const [taxes, setTaxes] = useState<Tax[]>([])
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [essentialExpenses, setEssentialExpenses] = useState<Expense[]>([])
  const [discretionaryExpenses, setDiscretionaryExpenses] = useState<Expense[]>([])
  const [generalExpenses, setGeneralExpenses] = useState<Expense[]>([])
  const [housingExpenses, setHousingExpenses] = useState<Expense[]>([])
  const [loanAndLiabilityExpenses, setLoanAndLiabilityExpenses] = useState<Expense[]>([])
  const [foodAndPersonalCareExpenses, setFoodAndPersonalCareExpenses] = useState<Expense[]>([])
  const [insuranceAndMedicalExpenses, setInsuranceAndMedicalExpenses] = useState<Expense[]>([])
  const [vehiclesAndTransportationExpenses, setVehiclesAndTransportationExpenses] = useState<Expense[]>([])
  const [travelAndEntertainmentExpenses, setTravelAndEntertainmentExpenses] = useState<Expense[]>([])
  const [givingAndMiscExpenses, setGivingAndMiscExpenses] = useState<Expense[]>([])
  const [currentBudgetCalc, setCurrentBudgetCalc] = useState<IBudgetCalc | undefined>()
  const [currentModel, setCurrentModel] = useState<Model | undefined>()
  const [currentYear, setCurrentYear] = useState<number>((new Date()).getFullYear())
  const [budgetType, setBudgetType] = useState<BudgetType>(BudgetType.Detailed)

  const {modelStore} = useResources()

  const theme = useTheme()
  const styles = {
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.grey["700"],
    },
  }

  useEffect(() => {
    if (model && budgetCalc) {
      setCurrentModel(model)
      setIncomes(model.incomes)
      setDeductions(model.deductions)
      setTaxes(model.taxes)
      const expenses = model.expenses
      setExpenses(expenses)
      setEssentialExpenses(expenses.filter((expense: Expense) => expense.discretionary === false))
      setDiscretionaryExpenses(expenses.filter((expense: Expense) => expense.discretionary === true))
      setGeneralExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.General))
      setHousingExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.Housing))
      setLoanAndLiabilityExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.LoansAndLiabilities))
      setFoodAndPersonalCareExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.FoodAndPersonalCare))
      setInsuranceAndMedicalExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.InsuranceAndMedical))
      setVehiclesAndTransportationExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.VehiclesAndTransportation))
      setTravelAndEntertainmentExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.TravelAndEntertainment))
      setGivingAndMiscExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.GivingAndMisc))
      setCurrentBudgetCalc(budgetCalc)
      setBudgetType(model.getSetting(ModelSetting.BudgetType, BudgetType.Detailed))
    }
  }, [model, budgetCalc])

  const getReportDetails = () => {
    const user = modelStore.currentUser
    if (user && currentModel) {
      const fullDate = dateToLocalFormat(new Date(), "MMM d, yyyy")
      return (
        <React.Fragment>
          <Box display="flex" alignItems="baseline">
            <Box width={50}><FormLabel sx={styles.formLabel}>Date:</FormLabel></Box>
            <Box><Typography variant="body2">{fullDate}</Typography></Box>
          </Box>
          <Box display="flex" alignItems="baseline">
            <Box width={50}><FormLabel sx={styles.formLabel}>Name:</FormLabel></Box>
            <Box><Typography variant="body2">{user!.fullName}</Typography></Box>
          </Box>
          <Box display="flex" alignItems="baseline">
            <Box width={50}><FormLabel sx={styles.formLabel}>Model:</FormLabel></Box>
            <Box><Typography variant="body2">{currentModel!.name} ({currentModel!.appliedPlan?.name})</Typography></Box>
          </Box>
        </React.Fragment>
      )
    } else {
      return null
    }
  }

  if (!currentModel || !currentBudgetCalc) {
    return null
  }

  return (
    <PrintReport isPrinting={true}
                 title={`${currentYear} Budget Report`}
                 details={getReportDetails()}
    >
      <BudgetReport user={modelStore.currentUser!}
                    model={currentModel}
                    incomes={incomes}
                    deductions={deductions}
                    taxes={taxes}
                    essentialExpenses={essentialExpenses}
                    discretionaryExpenses={discretionaryExpenses}
                    generalExpenses={generalExpenses}
                    housingExpenses={housingExpenses}
                    loanAndLiabilityExpenses={loanAndLiabilityExpenses}
                    foodAndPersonalCareExpenses={foodAndPersonalCareExpenses}
                    insuranceAndMedicalExpenses={insuranceAndMedicalExpenses}
                    vehiclesAndTransportationExpenses={vehiclesAndTransportationExpenses}
                    travelAndEntertainmentExpenses={travelAndEntertainmentExpenses}
                    givingAndMiscExpenses={givingAndMiscExpenses}
                    budgetCalc={currentBudgetCalc}
                    budgetType={budgetType}
      />
    </PrintReport>
  )
}

export default BudgetPrintReport